import { ReactNode } from 'react';
import { Alert, Box } from '@mui/material';
import ClinicalLockArea from './ClinicalLockArea';
import { useClinicalRecordLock } from './ClinicalRecordLockContext';

/**
 * Tabs whose forms are part of the signed clinical record and can be amended.
 * Handover tabs are amendable too, but only once submitted — see `handoverTabs`.
 */
export const AMENDABLE_TAB_IDS: ReadonlySet<string> = new Set([
  'prescreening',
  'history_and_symptoms',
  'refraction',
  'examination',
  'cl_h_and_s',
  'anterior_eye',
  'cl_fitting',
  'cl_aftercare',
  'teach_screen',
  'des',
  'mecs',
  'grr',
]);

type Props = {
  tabs: { id: string }[];
  activeTab: string;
  renderTab: (tabId: string) => ReactNode;
  /**
   * Handover tabs → whether that handover has already been submitted. Handover
   * is completed after sign-off, so it stays editable until submitted once.
   */
  handoverTabs: Record<string, boolean>;
};

/**
 * Renders the active clinical tab inside a ClinicalLockArea. While amending,
 * every amendable tab stays mounted (hidden when inactive) so edits made on one
 * tab survive switching to another before the amendment is signed.
 */
export default function ClinicalTabPanels({
  tabs,
  activeTab,
  renderTab,
  handoverTabs,
}: Props) {
  const lock = useClinicalRecordLock();
  const isHandover = (id: string) =>
    Object.prototype.hasOwnProperty.call(handoverTabs, id);
  /** A handover can only be amended once it has been submitted. */
  const isSubmittedHandover = (id: string) =>
    isHandover(id) && !!handoverTabs[id];

  const lockedFor = (id: string) => {
    if (isHandover(id)) {
      // Not submitted yet: the normal post-sign-off handover flow still applies.
      if (!isSubmittedHandover(id)) return false;
      return !lock.isAmending && lock.isSigned;
    }
    if (AMENDABLE_TAB_IDS.has(id)) return lock.isLocked;
    return false;
  };

  if (!lock.isAmending) {
    return (
      <ClinicalLockArea key={activeTab} locked={lockedFor(activeTab)}>
        {renderTab(activeTab)}
      </ClinicalLockArea>
    );
  }

  // While amending, keep every amendable panel mounted so edits made on one tab
  // survive switching to another before the amendment is signed.
  const amendable = tabs.filter(
    (t) => AMENDABLE_TAB_IDS.has(t.id) || isSubmittedHandover(t.id),
  );
  const activeIsAmendable = amendable.some((t) => t.id === activeTab);
  return (
    <>
      {amendable.map((t) => (
        <Box key={t.id} sx={{ display: t.id === activeTab ? 'block' : 'none' }}>
          <ClinicalLockArea locked={false}>{renderTab(t.id)}</ClinicalLockArea>
        </Box>
      ))}
      {!activeIsAmendable && (
        <>
          {isHandover(activeTab) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This handover has not been submitted yet, so there is nothing to
              amend here. Cancel the amendment to submit it.
            </Alert>
          )}
          <ClinicalLockArea key={activeTab} locked={lockedFor(activeTab)}>
            {renderTab(activeTab)}
          </ClinicalLockArea>
        </>
      )}
    </>
  );
}
